/**
 * FuarAlaniOrgDugumModal.jsx — add or edit one person of a fairground's organisation chart (backlog #51 follow-up, 2026-10-20).
 *
 * Mounted only while open (fields start from the person being edited, nothing to reset). One modal for both: `dugum` null = add,
 * a node = edit. "Reports to" lists every person of the chart (indented by depth) except the person and everyone below them, so a
 * cycle cannot be picked; the backend refuses one anyway (400) and its message is shown if it ever happens.
 */
import React, { useState } from "react";
import { Alert, Button, Col, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { addFuarAlaniOrgNode, hataMetni, updateFuarAlaniOrgNode } from "../../services/fuarAlaniEkBilgiService";

/** The chart as a flat list in tree order: [{ id, adSoyad, depth }]. */
export function duzlestir(nodes, depth = 0, out = []) {
  for (const n of nodes || []) {
    out.push({ id: n.id, adSoyad: n.adSoyad, depth });
    duzlestir(n.children, depth + 1, out);
  }
  return out;
}

/** The ids of a person and everybody below them. */
export function altAgac(dugum, out = new Set()) {
  if (!dugum) return out;
  out.add(dugum.id);
  (dugum.children || []).forEach((c) => altAgac(c, out));
  return out;
}

export default function FuarAlaniOrgDugumModal({ fuarAlaniId, dugum, varsayilanParentId = null, tumu = [], onClose, onChanged }) {
  const { t: tx } = useTranslation("yonetim");
  const duzenleme = !!dugum;
  const [adSoyad, setAdSoyad] = useState(dugum?.adSoyad || "");
  const [unvan, setUnvan] = useState(dugum?.unvan || "");
  const [telefon, setTelefon] = useState(dugum?.telefon || "");
  const [email, setEmail] = useState(dugum?.email || "");
  const [parentId, setParentId] = useState(String(duzenleme ? (dugum.parentId ?? "") : (varsayilanParentId ?? "")));
  const [hata, setHata] = useState(null);
  const [kaydediliyor, setKaydediliyor] = useState(false);

  const yasak = altAgac(dugum);
  const secenekler = tumu.filter((n) => !yasak.has(n.id));

  async function kaydet() {
    if (!adSoyad.trim()) { setHata(tx("fuarAlaniDetay.adSoyadZorunlu")); return; }
    setKaydediliyor(true);
    setHata(null);
    const govde = { adSoyad: adSoyad.trim(), unvan, telefon, email, parentId: parentId === "" ? null : Number(parentId) };
    try {
      if (duzenleme) await updateFuarAlaniOrgNode(fuarAlaniId, dugum.id, govde);
      else await addFuarAlaniOrgNode(fuarAlaniId, govde);
      await onChanged?.();
      onClose();
    } catch (err) {
      setHata(hataMetni(err, tx("fuarAlaniDetay.kisiKaydedilemedi")));
      setKaydediliyor(false);
    }
  }

  return (
    <Modal isOpen toggle={onClose} centered>
      <ModalHeader toggle={onClose}>{duzenleme ? tx("fuarAlaniDetay.kisiDuzenle") : tx("fuarAlaniDetay.kisiEkle")}</ModalHeader>
      <ModalBody>
        {hata && <Alert color="danger" className="small py-2">{hata}</Alert>}
        <Row className="g-3">
          <Col xs={12}>
            <Label className="form-label" for="fa-org-ad">{tx("fuarAlaniDetay.adSoyad")} *</Label>
            <Input id="fa-org-ad" maxLength={255} value={adSoyad} onChange={(e) => setAdSoyad(e.target.value)} />
          </Col>
          <Col xs={12}>
            <Label className="form-label" for="fa-org-unvan">{tx("fuarAlaniDetay.unvan")}</Label>
            <Input id="fa-org-unvan" maxLength={255} value={unvan} onChange={(e) => setUnvan(e.target.value)} />
          </Col>
          <Col sm={5}>
            <Label className="form-label" for="fa-org-tel">{tx("fuarAlaniDetay.telefon")}</Label>
            <Input id="fa-org-tel" maxLength={50} value={telefon} onChange={(e) => setTelefon(e.target.value)} />
          </Col>
          <Col sm={7}>
            <Label className="form-label" for="fa-org-eposta">{tx("fuarAlaniDetay.eposta")}</Label>
            <Input id="fa-org-eposta" type="email" maxLength={255} value={email} onChange={(e) => setEmail(e.target.value)} />
          </Col>
          <Col xs={12}>
            <Label className="form-label" for="fa-org-ust">{tx("fuarAlaniDetay.bagliOlduguKisi")}</Label>
            <Input id="fa-org-ust" type="select" value={parentId} onChange={(e) => setParentId(e.target.value)}>
              <option value="">{tx("fuarAlaniDetay.ustDuzey")}</option>
              {secenekler.map((n) => (
                <option key={n.id} value={n.id}>{`${"  ".repeat(n.depth)}${n.depth ? "└ " : ""}${n.adSoyad}`}</option>
              ))}
            </Input>
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={onClose} disabled={kaydediliyor}>{tx("common:iptal")}</Button>
        <Button color="primary" onClick={kaydet} disabled={kaydediliyor}>
          {kaydediliyor ? <Spinner size="sm" /> : tx("common:kaydet")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
